
import { CheckCircle2, MousePointerClick, LineChart } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BeginnerSolution = () => {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-white to-blue-50 border-t border-b border-blue-100">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-blue-600 text-white border-blue-700 mb-4">
            The Solution
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            No more <span className="text-blue-600">guessing</span> with keywords or costly mistakes
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We've demystified Google Ads with a simple, step-by-step process that prevents expensive mistakes and gets your first campaign live in 5 minutes.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
          <div className="order-2 md:order-1">
            <h3 className="text-2xl font-bold mb-6">Step-by-step guidance that prevents mistakes</h3>
            <p className="text-lg text-muted-foreground mb-8">
              Our guided process walks you through each step in plain English, preventing the common errors that waste money and ensuring you understand exactly what you're doing.
            </p>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">No confusing technical terms</h4>
                  <p className="text-muted-foreground">Everything is explained in simple language, so you never feel lost or overwhelmed.</p>
                </div>
              </li>
              
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Pre-built templates</h4>
                  <p className="text-muted-foreground">Start with proven campaign structures designed for different business goals.</p>
                </div>
              </li>
              
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Smart mistake prevention</h4>
                  <p className="text-muted-foreground">Our AI catches common errors before they cost you money, with built-in safeguards for beginners.</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="order-1 md:order-2">
            <div className="bg-white rounded-xl shadow-xl border border-blue-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 py-3 px-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <div className="mx-auto text-sm text-gray-500">Campaign Creator</div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-2">What's your business about?</h3>
                  <p className="text-sm text-gray-500 mb-4">Tell us a bit about your business so we can create relevant ads.</p>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm">I run a local bakery specializing in custom cakes and pastries. We offer delivery and pickup options.</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-2">What's your goal?</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-600 text-white rounded-lg p-4 border-2 border-blue-700 flex items-center">
                      <MousePointerClick className="h-5 w-5 mr-2" /> More Website Visitors
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 flex items-center">
                      <LineChart className="h-5 w-5 mr-2 text-gray-500" /> More Sales
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-2">Creating your campaign...</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      <span>Analyzing your business</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      <span>Finding relevant keywords</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>Writing compelling ad copy</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="bg-white rounded-xl shadow-xl border border-blue-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 py-3 px-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <div className="mx-auto text-sm text-gray-500">Campaign Results</div>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800">Your campaign is ready to launch!</span>
                </div>
                
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-2">Your Ad Preview</h3>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="text-xs text-gray-500 mb-1">Ad • www.localcakebakery.com</div>
                    <h4 className="text-blue-700 font-medium mb-1">Custom Cakes & Pastries | Same-Day Delivery Available</h4>
                    <p className="text-sm text-gray-600">Order custom cakes & pastries for any occasion. Local pickup or delivery options available. Order online now!</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-lg mb-2">Top Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full">custom cakes</span>
                    <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full">bakery near me</span>
                    <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full">cake delivery</span>
                    <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full">birthday cake</span>
                    <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full">pastry shop</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold mb-6">See results immediately</h3>
            <p className="text-lg text-muted-foreground mb-8">
              Once your campaign is created, you can launch it with one click and start seeing results the same day.
            </p>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Direct Google Ads publishing</h4>
                  <p className="text-muted-foreground">Publish your campaign directly to Google with one click. No copying and pasting required.</p>
                </div>
              </li>
              
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Easy performance tracking</h4>
                  <p className="text-muted-foreground">Monitor your ad performance with simple, easy-to-understand reports.</p>
                </div>
              </li>
              
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-1 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Beginner-friendly insights</h4>
                  <p className="text-muted-foreground">Get actionable recommendations in plain English to improve your campaigns.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeginnerSolution;
